import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import 'swiper/css';
import { DEFAULT_LAST_READING_CHAPTER, additionalBook, chapterId, content } from '../../constants/stringsAndFields';
import DataContext from '../../context/dataContext';
import { useTheme } from '../../theme/ThemeContext';
import { getIndexByChapterId, isNotNullNumber } from '../../utils/arrays';
import { copyToClipboard } from '../../utils/clipboard';
import {
    registerEvents,
    registerSelectionToolbar,
} from '../../utils/html/eventListeners';
import { generateChapterHtml } from '../../utils/html/html';
import { setChapterTitle } from '../../utils/navigation';
import { generateFavoriteQuote } from '../../utils/notes';
import { getBookmarkReading, saveLastReading } from '../../utils/storage';
import { getVerseElementId } from '../../utils/webview';
import Icon from '../ui-components/Icon';
import { UIConnectionStatus } from '../ui-components/UIConnectionStatus';
import { UIPressable } from '../ui-components/UIPressable';
import UIScreenReaderWrapper from '../ui-components/UIScreenReadingWrapper';
import { AudioBottomSheet } from './AudioBottomSheet';
import EditNoteModal from './EditNoteModal';
import FootnoteModal from './FootnoteModal';
import { delay } from '../../utils/promise';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import throttle from 'lodash/throttle';
import globalStyle from '../../theme/globalStyle';
import { getBottomTabBarStyles } from '../../theme/tabBarStyles';

const scrollCallback = throttle((e, setButtonsVisible, offsetRef, buttonsVisible, _nav, _style, setHideTabBar) => {
    const currentOffset = e.nativeEvent.contentOffset.y;
    const prevOffset = offsetRef.current;
    const direction = currentOffset > prevOffset ? 'down' : 'up';

    const threshold = 10;

    if (direction === 'up' && Math.abs(currentOffset - prevOffset) > threshold) {
        setButtonsVisible(true);
        setHideTabBar(false);
    } else if (direction === 'down') {
        setButtonsVisible(false);
        setHideTabBar(true);
    }

    offsetRef.current = currentOffset;
}, 150);

const tabHiddenStyle = {
    display: 'none',
};

const tabDefaultStyle = {
    display: 'flex',
};

const useScrollHandler = (setHideTabBar) => {
    const offsetRef = useRef(0);
    const [buttonsVisible, setButtonsVisible] = useState(true);

    const width = useWindowDimensions().width;
    const isDesktop = width > 1200;

    return {
        offset: offsetRef.current,
        buttonsVisible,
        scrollCallback: e => {
            if (isDesktop) return;
            scrollCallback(e, setButtonsVisible, offsetRef, buttonsVisible, null, null, setHideTabBar);
        },
    };
};

const ReadingView = ({
    readingPlanData = null,
    verse = null,
    setVerses,
    isReadingPlan = false,
    setReadingVerse = () => {},
    chapterIndex = null,
    jumpToPageComplete = false,
    setJumpToPageComplete = () => {},
    restoredLastReading = false, // @Samuel This is not used
    setRestoredLastReading = () => {}, // @Samuel This is not used
    disableViewPager = false, // @Samuel This is not used
    setShowSoundIcon,
    setBookmarkedReading,
    navigation,
    audioModalVisible,
    setAudioModalVisible,
}) => {
    const {
        getFootnote,
        saveFavoriteQuote,
        getBookByName,
        getNextChapter,
        getPreviousChapter,
        chapters,
        getVersesByChapter,
        showConnectionWarning,
        setHideTabBar,
    } = useContext(DataContext);
    const slideRef = useRef(null);
    const { theme, fontStyle, fontSize } = useTheme();
    const [footNoteModalVisible, setFootNoteModalVisible] = useState(false);
    const [noteEditModalVisible, setNoteEditModalVisible] = useState(false);
    const [noteVerses, setNoteVerses] = useState(null);
    const [htmlContent, setHtmlContent] = useState('');
    const [readingChapter, setReadingChapter] = useState('');
    const [footnote, setFootnote] = useState(null);
    const [showMessage, setShowMessage] = useState(false);
    const [messageContent, setMessageContent] = useState('');

    const [currentIndex, setCurrentIndex] = useState(null);
    const isFirstChapter = readingChapter?.[chapterId] === DEFAULT_LAST_READING_CHAPTER;

    const global = globalStyle(theme);
    const styles = getStyles(theme);

    const width = useWindowDimensions().width;
    const isDesktop = width > 1200;
    const isDesktopLarge = width > 1600;

    useEffect(() => {
        if (!jumpToPageComplete && isNotNullNumber(chapterIndex) && chapters) {
            setReadingChapter(chapters[chapterIndex]);
            setJumpToPageComplete(true);
        }
    }, [jumpToPageComplete, chapterIndex]);

    useEffect(() => {
        if (readingPlanData) {
            setHtmlContent(readingPlanData[content]);
        }
    }, [readingPlanData]);

    useEffect(() => {
        if (readingChapter && chapters) {
            setHtmlContent(generateChapterHtml(readingChapter, false, fontStyle, fontSize, theme));
            const chapterIdx = getIndexByChapterId(chapters, readingChapter[chapterId]);
            setChapterTitle(chapterIdx, chapters, navigation);
            setShowSoundIcon(!readingChapter?.[additionalBook]);
            getBookmarkReading().then(bookmark => {
                if (bookmark && bookmark === readingChapter?.[chapterId]) {
                    setBookmarkedReading(true);
                } else {
                    setBookmarkedReading(false);
                }
            });
            saveLastReading(readingChapter[chapterId]).catch(console.error);
            getVersesByChapter(readingChapter).then(v => {
                setVerses(v);
            });
            setCurrentIndex(chapterIdx);
        }
    }, [readingChapter, fontStyle, fontSize, theme]);

    // Single unified effect: register footnote/arrow events + selection toolbar
    useEffect(() => {
        if (!slideRef.current) return;

        const cleanupEvents = registerEvents(
            slideRef.current,
            interfaceExecuteCall
        );

        const cleanupSelection = registerSelectionToolbar(
            slideRef.current,
            theme,
            handleSelectionAction
        );

        return () => {
            cleanupEvents();
            cleanupSelection();
        };
    }, [htmlContent, theme]);

    const openPreviousChapter = async _ => {
        if (readingChapter) {
            const c = await getPreviousChapter(readingChapter[chapterId], false);
            if (c) {
                setReadingChapter(c);
            }
        }
    };

    const openNextChapter = async _ => {
        if (readingChapter) {
            const c = await getNextChapter(readingChapter[chapterId], false);
            if (c) {
                setReadingChapter(c);
            }
        }
    };

    const interfaceExecuteCall = (e, t, data) => {
        if (e === 'activateFootnote') {
            getFootnote(t).then(res => {
                setFootnote(res);
                setFootNoteModalVisible(true);
            });
        } else if (e === 'nextChapter') {
            openNextChapter().catch(console.error);
        }
    };

    // Handle toolbar actions — called from pure DOM toolbar, no React state for selection
    const handleSelectionAction = useCallback((action, { text, verseRange }) => {
        if (action === 'save') {
            generateFavoriteQuote(
                readingChapter,
                text,
                getBookByName,
                saveFavoriteQuote,
                verseRange
            )
                .then(_ => showTemporaryMessage('Citat je uspješno spremljen'))
                .catch(e => {
                    console.log(e);
                    showTemporaryMessage('Došlo je do pogreške pri spremanju citata');
                });
        } else if (action === 'note') {
            setNoteVerses(verseRange);
            setNoteEditModalVisible(true);
        } else if (action === 'copy') {
            copyToClipboard(text);
            showTemporaryMessage('Citat je uspješno kopiran');
        }
    }, [readingChapter]);

    const showTemporaryMessage = message => {
        setMessageContent(message);
        setShowMessage(true);

        setTimeout(() => {
            setShowMessage(false);
        }, 3000);
    };

    const { scrollCallback, buttonsVisible } = useScrollHandler(setHideTabBar);

    const buttonLeftStyle = {
        transform: buttonsVisible ? 'translateX(0)' : 'translateX(-100%)',
        opacity: buttonsVisible ? 1 : 0,
        transition: 'transform 0.3s',
    };

    const buttonRightStyle = {
        transform: buttonsVisible ? 'translateX(0)' : 'translateX(100%)',
        opacity: buttonsVisible ? 1 : 0,
        transition: 'transform 0.3s',
    };

    const playerHiddenStyle = {
        transform: buttonsVisible ? 'translateY(0)' : 'translateY(55px)',
        transition: 'transform 0.3s',
    };

    const navLeftStyle = {
        left: isDesktopLarge ? 290 : isDesktop ? 250 : 20,
    };

    return (
        <>
            <>
                {!readingPlanData && !isFirstChapter && (
                    <UIPressable
                        onPress={openPreviousChapter}
                        style={[styles.navButton, !isDesktop && buttonLeftStyle, navLeftStyle]}>
                        <Icon name="arrow-left" size={20} color={theme.colors.text} />
                    </UIPressable>
                )}

                {!readingPlanData && (
                    <UIPressable
                        onPress={openNextChapter}
                        style={[styles.navButton, !isDesktop && buttonRightStyle, { right: isDesktopLarge ? 50 : 20 }]}>
                        <Icon name="arrow-right" size={20} color={theme.colors.text} />
                    </UIPressable>
                )}
            </>

            <UIScreenReaderWrapper scrollCallback={scrollCallback} onScroll={scrollCallback}>
                {showConnectionWarning && <UIConnectionStatus />}

                <View
                    style={[
                        isDesktop && styles.wrapperDesktop,
                        { maxWidth: 1200, marginLeft: 'auto', marginRight: 'auto' },
                    ]}>
                    <div dangerouslySetInnerHTML={{ __html: htmlContent }} style={{ width: '100%' }} ref={slideRef} />
                </View>
                <FootnoteModal
                    modalVisible={footNoteModalVisible}
                    setModalVisible={setFootNoteModalVisible}
                    footnoteContent={footnote}
                />
                <EditNoteModal
                    modalVisible={noteEditModalVisible}
                    setModalVisible={setNoteEditModalVisible}
                    chapter={readingChapter}
                    selectedVerses={noteVerses}
                    currentIndex={currentIndex}
                />
            </UIScreenReaderWrapper>

            {showMessage && (
                <View style={styles.messageBox}>
                    <Text style={[global.textBold, styles.messageText]}>{messageContent}</Text>
                </View>
            )}

            <AudioBottomSheet
                modalVisible={audioModalVisible}
                setModalVisible={setAudioModalVisible}
                style={[!isReadingPlan && !isDesktop && playerHiddenStyle]}
                isReadingPlan={isReadingPlan}
            />
        </>
    );
};

const getStyles = theme =>
    StyleSheet.create({
        navButton: {
            position: 'fixed',
            top: '50%',
            marginTop: -25,
            zIndex: 1,
            width: 50,
            height: 50,
            backgroundColor: theme.colors.navBackground,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 50,
            shadowColor: theme.colors.shadow,
            shadowOpacity: 0.4,
            shadowOffset: { width: 0, height: 0 },
            shadowRadius: 10,
            elevation: 5,
            transition: 'transform 0.3s',
        },

        wrapperDesktop: {
            paddingHorizontal: 40,
        },

        messageBox: {
            position: 'absolute',
            top: 20,
            left: '50%',
            transform: [{ translateX: -140 }],
            width: 280,
            padding: 10,
            backgroundColor: theme.colors.navBackground,
            borderRadius: 10,
            alignItems: 'center',
            shadowColor: theme.colors.shadow,
            shadowOpacity: 0.9,
            shadowOffset: { width: 0, height: 4 },
            shadowRadius: 10,
            elevation: 5,
        },

        messageText: {
            color: theme.colors.primary,
        },
    });

export default ReadingView;
